// Oike — the versions of a quote and what they add up to (docs/servicios/cotizaciones.md, 5.3).
// A revision is another Diagnostic Intake record that points to the previous version
// (Revision Of, one link). Here the records are grouped into chains, the current version of
// each chain is identified, and the totals are counted once per chain:
// - a chain is a sale once: by its latest accepted version (an accepted version that was later
//   revised and re-accepted is history, never a second sale);
// - payments stay on the version where they were registered; a chain counts as paid, partly paid
//   or pending once, never once per version; nothing is copied between versions;
// - amounts are added per currency (never converted) and one-off and monthly apart;
// - a record pointing to more than one previous version, or a version revised twice, is an
//   anomaly to review: it is shown, never resolved by picking one.
// Personal quotes (Quote Recipient = person) and company quotes are kept apart (kindOf).
// Pure: the screens and scripts/test-quote-chains.mjs use the same functions.
import { revisionLinksOf, isAccepted, wasAccepted } from './quoteRules.js';
import { classifyRecipient, hasQuote } from './personQuotes.js';
import { FACT_FIELDS, acceptanceMarksOf } from './pipelineFacts.js';

const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
const name = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
export { hasQuote };

/** 'personal', 'company' or 'review' (src/personQuotes.js › classifyRecipient): never guessed. */
export const kindOf = (f = {}) => classifyRecipient(f).kind;

/** One-off and monthly totals of a version, from its own lines. */
export function totalsOf(f = {}) {
  let lines = [];
  try { lines = JSON.parse(f['Quote Lines JSON'] || '[]'); } catch { lines = []; }
  if (!Array.isArray(lines)) lines = [];
  const once = lines.reduce((s, l) => s + (Number(l?.onceFee) || 0), 0);
  const monthly = lines.reduce((s, l) => s + (Number(l?.monthlyFee) || 0), 0);
  return { currency: f['Quote Currency'] || 'USD', once, monthly };
}

/** The payment registered on one version (its own fields only). */
export function paymentOf(f = {}) {
  const status = name(f[FACT_FIELDS.paymentStatus]);
  const at = f[FACT_FIELDS.paymentReceivedAt] || '';
  if (at || status === 'Received') return { state: at ? 'received' : 'received-undated', at };
  if (status === 'Partial') return { state: 'partial', at: '' };
  if (status === 'Pending') return { state: 'pending', at: '' };
  return { state: 'none', at: '' };
}

/** What a version is, in words the screens show. */
export function versionStateOf(f = {}) {
  const m = acceptanceMarksOf(f[FACT_FIELDS.factsNote]);
  if (isAccepted(f)) return 'accepted';
  if (name(f['Quote Status']) === 'Lost' && (f[FACT_FIELDS.wonAt] || m.cancelled)) return 'cancelled';
  if (m.error) return 'acceptance-corrected';
  if (name(f['Quote Status']) === 'Lost') return 'lost';
  if (links(f['Consulting Landing']).length) return 'published';
  if (name(f['Quote Status']) === 'Approved') return 'approved';
  return 'draft';
}

/**
 * The chains of the quotes among `records`. Each chain: { id (its first version), kind, versions
 * (oldest first, each with its number), current (the version without a revision; null when the
 * chain branched), anomalies, sale (the latest accepted version, or null), payments }.
 */
export function chainsOf(records = []) {
  const quotes = records.filter(r => hasQuote(r.fields || {}));
  const byId = new Map(quotes.map(r => [r.id, r]));
  // The previous version of each record: only a single, existing link
  const parentOf = new Map();
  const anomalies = new Map(); // record id → codes
  const flag = (id, code) => anomalies.set(id, [...(anomalies.get(id) || []), code]);
  for (const r of quotes) {
    const prev = revisionLinksOf(r.fields || {});
    if (prev.length > 1) { flag(r.id, 'several-previous'); continue; }
    if (prev.length === 1) {
      if (byId.has(prev[0])) parentOf.set(r.id, prev[0]);
      else flag(r.id, 'previous-missing'); // not loaded or out of reach: shown on its own, never guessed
    }
  }
  const childrenOf = new Map();
  for (const [child, parent] of parentOf) childrenOf.set(parent, [...(childrenOf.get(parent) || []), child]);
  const rootOf = (id) => {
    const seen = new Set([id]);
    let cur = id;
    while (parentOf.has(cur)) {
      const p = parentOf.get(cur);
      if (seen.has(p)) { flag(id, 'cycle'); return [...seen].sort()[0]; }
      seen.add(p); cur = p;
    }
    return cur;
  };
  const groups = new Map();
  for (const r of quotes) { const root = rootOf(r.id); groups.set(root, [...(groups.get(root) || []), r]); }

  const order = (a, b) => (Number(a.fields?.['Revision Number']) || 0) - (Number(b.fields?.['Revision Number']) || 0)
    || String(a.createdTime || '').localeCompare(String(b.createdTime || ''));
  const chains = [];
  for (const [root, members] of groups) {
    const versions = [...members].sort(order).map((r, i) => ({
      record: r, id: r.id, number: Number(r.fields?.['Revision Number']) || i + 1,
      state: versionStateOf(r.fields || {}), totals: totalsOf(r.fields || {}), payment: paymentOf(r.fields || {}),
      anomalies: anomalies.get(r.id) || [],
    }));
    const chainAnomalies = [...new Set(versions.flatMap(v => v.anomalies))];
    const branched = versions.filter(v => (childrenOf.get(v.id) || []).length > 1);
    if (branched.length) chainAnomalies.push('revised-twice');
    const leaves = versions.filter(v => !(childrenOf.get(v.id) || []).length);
    const current = leaves.length === 1 ? leaves[0] : null;
    const accepted = versions.filter(v => v.state === 'accepted');
    const sale = accepted.length ? accepted[accepted.length - 1] : null;
    const paid = versions.filter(v => v.payment.state !== 'none');
    // The operative version: what the client has in hand. The accepted one; otherwise the latest
    // published one while a later revision is still in preparation; otherwise the current one.
    // A draft never hides what was already sent.
    const published = versions.filter(v => v.state === 'published');
    const latestPublished = published.length ? published[published.length - 1] : null;
    const operative = current?.state === 'accepted' ? current
      : sale || (latestPublished && (!current || ['draft', 'approved'].includes(current.state)) ? latestPublished : current) || null;
    const later = operative ? versions.filter(v => versions.indexOf(v) > versions.indexOf(operative)) : [];
    const salePayment = sale ? sale.payment : null;
    chains.push({
      id: root, kind: kindOf((current || versions[versions.length - 1]).record.fields || {}),
      versions, current, anomalies: chainAnomalies, sale, operative,
      // A newer version still in preparation (not published, not accepted): shown as a notice
      pendingRevision: current && operative && current.id !== operative.id && ['draft', 'approved'].includes(current.state) ? current : null,
      // Earlier versions sent or accepted that a later operative version replaced
      replaced: operative ? versions.filter(v => versions.indexOf(v) < versions.indexOf(operative) && ['published', 'accepted'].includes(v.state)).map(v => v.id) : [],
      later: later.map(v => v.id),
      // Payments stay on their own version, never copied
      payments: paid.map(v => ({ versionId: v.id, number: v.number, ...v.payment })),
      // The payment of what counts now: only the accepted version's own payment. A payment of an
      // earlier version never confirms a revision with another price.
      paymentState: !sale ? 'none' : salePayment.state.startsWith('received') ? 'received' : salePayment.state === 'partial' ? 'partial' : 'pending',
      paymentHistory: paid.filter(v => v.id !== sale?.id).map(v => ({ versionId: v.id, number: v.number, ...v.payment })),
      everAccepted: versions.some(v => wasAccepted(v.record.fields || {})),
    });
  }
  return chains.sort((a, b) => String((b.current || b.versions[b.versions.length - 1]).record.createdTime || '').localeCompare(String((a.current || a.versions[a.versions.length - 1]).record.createdTime || '')));
}

/**
 * The summary of some chains: sales by currency (each chain once, by its latest accepted version),
 * and how many sales have their own payment completed, partial or not registered (by the accepted
 * version only). Never a sum across currencies, never an amount collected (there are no payment amounts).
 */
export function summaryOf(chains = []) {
  const byCurrency = {};
  const counts = { chains: chains.length, sold: 0, received: 0, partial: 0, pending: 0, anomalies: 0 };
  for (const c of chains) {
    if (c.anomalies.length) counts.anomalies++;
    if (!c.sale) continue;
    counts.sold++;
    const t = c.sale.totals;
    const cur = (byCurrency[t.currency] = byCurrency[t.currency] || { once: 0, monthly: 0, chains: 0 });
    cur.once += t.once; cur.monthly += t.monthly; cur.chains++;
    if (c.paymentState === 'received') counts.received++;
    else if (c.paymentState === 'partial') counts.partial++;
    else counts.pending++;
  }
  return { byCurrency, counts };
}

/** The current versions (one per chain) among some records: what lists and stats show, never every revision. */
export function currentVersionsOf(records = []) {
  return chainsOf(records).map(c => (c.current || c.versions[c.versions.length - 1]).record);
}

/** What the screens say about payments of a chain (never an amount: only what was registered). */
export function paymentNotesOf(chain) {
  const notes = [];
  if (chain.sale && chain.paymentState === 'received') notes.push({ tone: 'ok', text: `El importe vigente (v${chain.sale.number}) está pagado por completo, según el pago registrado en esa versión.` });
  else if (chain.sale && chain.paymentState === 'partial') notes.push({ tone: 'warn', text: `Pago parcial registrado en la versión vigente (v${chain.sale.number}).` });
  else if (chain.sale) notes.push({ tone: 'warn', text: `La versión vigente (v${chain.sale.number}) no tiene un pago registrado.` });
  if (chain.paymentHistory.length) notes.push({ tone: 'info', text: `Esta cotización tiene un pago registrado en su historial (${chain.paymentHistory.map(p => `v${p.number}`).join(', ')}). No confirma el pago de otra versión.` });
  return notes;
}
